<script setup lang="ts">
/**
 * Checkbox met label.
 *
 * - `variant="field"` (standaard): vervangt `<v-input type="checkbox">`, in het
 *   formuliergrid met fouten/voorbeeld/info eronder.
 * - `variant="inline"`: vervangt Components/Form/Checkbox-input: compacte rij zonder
 *   grid, met het label links of rechts.
 */
import { computed, ref } from 'vue'
import VFormField from './VFormField.vue'
import type { FormFieldBaseProps } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<boolean | number | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps & {
            /** Model is 1/0 i.p.v. true/false (tinyint-kolommen). */
            binary?: boolean
            variant?: 'field' | 'inline'
            /** Enkel voor `variant="inline"`. */
            labelPosition?: 'left' | 'right'
        }
    >(),
    {
        binary: false,
        variant: 'field',
        labelPosition: 'right',
        showErrors: true,
    }
)

const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const baseBindings = useFormFieldBindings(props, errors)

// Het label staat náást de checkbox (binnen het control), niet in de labelkolom.
const fieldBindings = computed(() => ({ ...baseBindings.value, label: null, indent: true }))

const inputRef = ref<HTMLInputElement | null>(null)

function focus(): void {
    inputRef.value?.focus()
}

defineExpose({ inputRef, focus })

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: false,
        hasSuffix: false,
        fullWidth: false,
    })
)

const trueValue = computed(() => (props.binary ? 1 : true))
const falseValue = computed(() => (props.binary ? 0 : false))

/**
 * Checkbox-input zette geen true-/false-value; enkel meegeven bij `binary`, zodat de
 * inline-variant dezelfde DOM houdt. (Een `undefined` binden kan niet: dan leest
 * v-model die als waarde.)
 */
const inlineValueBindings = computed(() =>
    props.binary ? { 'true-value': trueValue.value, 'false-value': falseValue.value } : {}
)
</script>

<template>
    <div v-if="variant === 'inline'" v-bind="rootAttrs" class="flex items-center gap-x-2">
        <label
            :for="id ?? name ?? undefined"
            class="flex items-center gap-x-2"
            :class="{
                'flex-row-reverse': labelPosition === 'left',
                'cursor-pointer': !disabled,
                'cursor-not-allowed opacity-50': disabled,
            }"
        >
            <input
                v-bind="{ ...controlAttrs, ...inlineValueBindings }"
                ref="inputRef"
                type="checkbox"
                :id="id ?? name ?? undefined"
                :name="name ?? undefined"
                v-model="modelValue"
                :required="required"
                :disabled="disabled"
                class="w-4 h-4 border-solid border-gray-300 rounded-none focus:border-primary focus:outline-none checked:bg-primary text-sm flex-auto disabled:cursor-not-allowed"
            />
            <span v-if="label" class="text-sm font-bold text-gray-700">{{ $t(label) }}</span>
        </label>
        <span v-if="required" class="text-danger">*</span>
    </div>

    <VFormField v-else v-bind="{ ...fieldBindings, ...rootAttrs }">
        <label :class="labelClasses" class="flex items-center cursor-pointer">
            <input
                v-bind="controlAttrs"
                ref="inputRef"
                v-model="modelValue"
                :id="id ?? name ?? undefined"
                :name="name ?? undefined"
                type="checkbox"
                :true-value="trueValue"
                :false-value="falseValue"
                :disabled="disabled"
                :class="classes"
            />
            <span v-if="label" class="flex-auto pl-2">{{ $t(label) }}</span>
            <span v-if="required" class="text-danger"> * </span>
        </label>
    </VFormField>
</template>

<style scoped>
/* Checkbox-input zette dezelfde focus-schaduw als .v-input input:focus; zonder .v-input
   rond de inline-variant moet die hier staan. */
input[type='checkbox']:focus {
    box-shadow: 0 0 3px var(--primary);
}
</style>
